import { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logo from "@/assets/codelumen-logo-3.webp";
import LanguageSwitcher from "./LanguageSwitcher";

interface NavItem {
  label: string;
  path?: string;
  children?: { label: string; path: string; description?: string }[];
}

const Navbar = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const location = useLocation();
  const dropdownTimeout = useRef<ReturnType<typeof setTimeout>>();

  const navItems: NavItem[] = [
    { label: t("nav.home"), path: "/" },
    {
      label: t("nav.industries"),
      children: [
        { label: t("nav.solutions"), path: "/solutions", description: t("nav.solutionsDesc") },
        { label: t("nav.dataCenter"), path: "/industries/data-center", description: t("nav.dataCenterDesc") },
        { label: t("nav.manufacturing"), path: "/solutions/manufacturing", description: t("nav.manufacturingDesc") },
        { label: t("nav.warehouse"), path: "/solutions/warehouse-distribution", description: t("nav.warehouseDesc") },
      ],
    },
    {
      label: t("nav.resources"),
      children: [
        { label: t("nav.calculator"), path: "/calculator", description: t("nav.calculatorDesc") },
        { label: t("nav.performanceLab"), path: "/performance-lab", description: t("nav.performanceLabDesc") },
        { label: t("nav.lifi"), path: "/lifi", description: t("nav.lifiDesc") },
      ],
    },
    { label: t("nav.about"), path: "/about" },
    { label: t("nav.contact"), path: "/contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setOpenDropdown(null);
    setMobileOpen(false);
    setMobileExpanded(null);
  }, [location.pathname]);

  const handleMouseEnter = (label: string) => {
    if (dropdownTimeout.current) clearTimeout(dropdownTimeout.current);
    setOpenDropdown(label);
  };

  const handleMouseLeave = () => {
    dropdownTimeout.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  const isChildActive = (children: NavItem["children"]) =>
    children?.some(c => location.pathname === c.path);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background border-b border-border shadow-lg"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-6 flex items-center justify-between h-20">
        <Link to="/" className="flex-shrink-0 -ml-4">
          <img src={logo} alt="CodeLumen Performance" className="w-48 h-auto" width={192} height={56} />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-1">
          {navItems.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => handleMouseEnter(item.label)}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  className={`px-3 py-2 text-xs font-semibold tracking-widest uppercase transition-colors flex items-center gap-1 ${
                    isChildActive(item.children)
                      ? "text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {item.label}
                  <ChevronDown className={`w-3 h-3 transition-transform ${openDropdown === item.label ? "rotate-180" : ""}`} />
                </button>

                {openDropdown === item.label && (
                  <div className={`absolute top-full pt-2 ${
                    item.label === t("nav.industries") ? "left-0 w-80" : "left-0 w-72"
                  }`}>
                    <div className="bg-card border border-border rounded-sm shadow-xl p-3">
                      {item.children!.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`block px-4 py-3 rounded-sm transition-colors group ${
                            location.pathname === child.path
                              ? "bg-secondary/80 text-foreground"
                              : "hover:bg-secondary/50 text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          <span className="text-xs font-semibold tracking-wider uppercase block">{child.label}</span>
                          {child.description && (
                            <span className="text-[10px] text-muted-foreground leading-relaxed mt-0.5 block">{child.description}</span>
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.path}
                to={item.path!}
                className={`px-3 py-2 text-xs font-semibold tracking-widest uppercase transition-colors ${
                  location.pathname === item.path
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {item.label}
              </Link>
            )
          )}
          <LanguageSwitcher />
          <Link
            to="/performance-lab#booking"
            onClick={() => {
              const el = document.getElementById("booking");
              if (el && location.pathname === "/performance-lab") {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }}
            className="ml-4 px-5 py-2 bg-primary text-primary-foreground text-xs font-bold tracking-widest rounded-sm hover:bg-primary/90 transition-all"
          >
            {t("nav.bookSession")}
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="lg:hidden p-2 text-foreground"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-background border-b border-border shadow-xl">
          <div className="container mx-auto px-6 py-4 flex flex-col gap-1">
            {navItems.map((item) =>
              item.children ? (
                <div key={item.label}>
                  <button
                    onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                    className={`w-full text-left px-3 py-3 text-sm font-semibold tracking-widest uppercase flex items-center justify-between rounded-sm ${
                      isChildActive(item.children)
                        ? "text-foreground bg-secondary/50"
                        : "text-muted-foreground"
                    }`}
                  >
                    {item.label}
                    <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpanded === item.label ? "rotate-180" : ""}`} />
                  </button>
                  {mobileExpanded === item.label && (
                    <div className="pl-4 flex flex-col gap-0.5">
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          onClick={() => setMobileOpen(false)}
                          className={`px-3 py-2.5 text-sm font-medium tracking-wider rounded-sm transition-colors ${
                            location.pathname === child.path
                              ? "text-foreground bg-secondary/50"
                              : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.path}
                  to={item.path!}
                  onClick={() => setMobileOpen(false)}
                  className={`text-left px-3 py-3 text-sm font-semibold tracking-widest uppercase transition-colors rounded-sm ${
                    location.pathname === item.path
                      ? "text-foreground bg-secondary/50"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                  }`}
                >
                  {item.label}
                </Link>
              )
            )}
            <div className="px-3 py-3">
              <LanguageSwitcher />
            </div>
            <Link
              to="/performance-lab#booking"
              onClick={() => {
                setMobileOpen(false);
                const el = document.getElementById("booking");
                if (el && location.pathname === "/performance-lab") {
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
                }
              }}
              className="mt-2 px-5 py-3 bg-primary text-primary-foreground text-sm font-bold tracking-widest rounded-sm text-center"
            >
              {t("nav.bookSession")}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
